<template lang="pug">
#fmaily-permitted-function-list
  .box
    .box-header
      .d-flex.justify-content-start.align-items-center
        .d-flex.justify-content-start.flex-column
          .title
            span 家族の使用機能（個別設定）
          .note
            span 家族ユーザーごとに使用機能の設定ができます。
            span.font-strong-color 家族ユーザーが使用するためには、使用機能の設定も使用中にする必要があります。
        .d-flex.justify-content-end.align-items-start.align-self-stretch.flex-fill
          button.btn.setting-btn.mr-2(type="button" @click="gotoSetting") 家族登録時設定
    .box-body
      b-tabs
        // opinion function edit tab
        b-tab.tab(title="家族からの連絡")
          FamilyPermittedFunctionEditTab(ref="familyOpinionPermittedFunctionEditTab"
            type="opinion"
            :errorMessage="familyOpinionPermittedFunctionEditTabErrorMessage" @onUpdate="confirmUpdateOpinionFunction")
        // picture function edit tab
        b-tab.tab(title="写真印刷依頼")
          FamilyPermittedFunctionEditTab(ref="familyPicturePermittedFunctionEditTab"
            type="picture"
            :errorMessage="familyPicturePermittedFunctionEditTabErrorMessage" @onUpdate="confirmUpdatePictureFunction")

  // facility update modals
  OkCancelModal(v-model="showOpinionFunctionUpdateModal" title="家族個別設定（家族からの連絡）を更新しますか？" @ok="updateOpinionFunction")
  OkModal(v-model="showOpinionFunctionUpdateSuccessModal" title="更新に成功しました。" @ok="refreshTabs")
  OkCancelModal(v-model="showPictureFunctionUpdateModal" title="家族個別設定（写真印刷依頼）を更新しますか？" @ok="updatePictureFunction")
  OkModal(v-model="showPictureFunctionUpdateSuccessModal" title="更新に成功しました。" @ok="refreshTabs")

  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";

import FacilitiesService from "@/commons/services/Facilities";
import FamiliesService, { FamilyGetModel, FamilyPatchModel } from "@/commons/services/Families";

import FamilyPermittedFunctionEditTab from "@/pc/containers/settings/FamilyPermittedFunctionEditTab.vue";

import PageLoading from "@/pc/basics/PageLoading.vue";
import OkCancelModal from "@/pc/basics/OkCancelModal.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import { PcUrl } from "@/commons/constants/PcUrl";

/**
 * ### 家族の機能使用可否管理画面コンポーネント
 */
@Component({
  components: {
    PageLoading,
    OkCancelModal,
    OkModal,
    FamilyPermittedFunctionEditTab
  }
})
export default class FamilyPermittedFunctionList extends Vue {
  /** 施設サービス */
  @Inject("FacilitiesService") facilitiesService!: FacilitiesService;
  /** 家族ユーザーサービス */
  @Inject("FamiliesService") familiesService!: FamiliesService;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ページ全体ローディングメッセージ */
  loadingMessage: string = "読込み中...";

  /** 「家族からの連絡」機能使用可否更新モーダル表示可否 */
  showOpinionFunctionUpdateModal: boolean = false;
  /** 更新対象の「家族からの連絡」機能使用可否を持つ家族 */
  updateOpinionFunctionTargetFamilies: Array<FamilyGetModel> = [];
  /** 「家族からの連絡」機能使用可否更新完了モーダル表示可否 */
  showOpinionFunctionUpdateSuccessModal: boolean = false;
  /** 「家族からの連絡」機能使用可否タブエラーメッセージ */
  familyOpinionPermittedFunctionEditTabErrorMessage: string = "";

  /** 「写真印刷依頼」機能使用可否更新モーダル表示可否 */
  showPictureFunctionUpdateModal: boolean = false;
  /** 更新対象の「写真印刷依頼」機能使用可否を持つ家族 */
  updatePictureFunctionTargetFamilies: Array<FamilyGetModel> = [];
  /** 「写真印刷依頼」機能使用可否更新完了モーダル表示可否 */
  showPictureFunctionUpdateSuccessModal: boolean = false;
  /** 「写真印刷依頼」機能使用可否タブエラーメッセージ */
  familyPicturePermittedFunctionEditTabErrorMessage: string = "";

  /**
   * ### 設定画面へ遷移する
   */
  public gotoSetting(): void {
    this.$router.push(PcUrl.DefaultFamilyPermittedFunctionEdit);
    return;
  }

  /**
   * ### 「家族からの連絡」機能使用可否の更新確認モーダルを表示する
   */
  public confirmUpdateOpinionFunction(families: Array<FamilyGetModel>): void {
    this.updateOpinionFunctionTargetFamilies = families;
    this.showOpinionFunctionUpdateModal = true;

    return;
  }

  /**
   * ### 「写真印刷依頼」機能使用可否の更新確認モーダルを表示する
   */
  public confirmUpdatePictureFunction(families: Array<FamilyGetModel>): void {
    this.updatePictureFunctionTargetFamilies = families;
    this.showPictureFunctionUpdateModal = true;

    return;
  }

  /**
   * ### 「家族からの連絡」機能使用可否を更新する
   */
  public async updateOpinionFunction(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "更新中...";

    const updateModels: Array<FamilyGetModel> = this.updateOpinionFunctionTargetFamilies as Array<FamilyGetModel>;

    const updateModelsList: Array<Array<FamilyGetModel>> = this.sliceUpdateModels(updateModels, 50);
    for (const models of updateModelsList) {
      let promises: Array<Promise<void>> = [];

      for (const model of models) {
        const requestBody: FamilyPatchModel = this.createFamilyPatchModel(model);
        promises.push(this.familiesService.patch(userStore, model.id, model.familyId, requestBody));
      }

      await Promise.all(promises);
    }

    this.showOpinionFunctionUpdateSuccessModal = true;
    this.isBusy = false;

    return;
  }

  /**
   * ### 「写真印刷依頼」機能使用可否を更新する
   */
  public async updatePictureFunction(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "更新中...";

    const updateModels: Array<FamilyGetModel> = this.updatePictureFunctionTargetFamilies as Array<FamilyGetModel>;

    const updateModelsList: Array<Array<FamilyGetModel>> = this.sliceUpdateModels(updateModels, 50);
    for (const models of updateModelsList) {
      let promises: Array<Promise<void>> = [];

      for (const model of models) {
        const requestBody: FamilyPatchModel = this.createFamilyPatchModel(model);
        promises.push(this.familiesService.patch(userStore, model.id, model.familyId, requestBody));
      }

      await Promise.all(promises);
    }

    this.showPictureFunctionUpdateSuccessModal = true;
    this.isBusy = false;

    return;
  }

  /**
   * ### リクエストを並列実行するが、chromeの同時リクエスト数に上限があるので分割する
   */
  public sliceUpdateModels(updateModels: Array<FamilyGetModel>, sliceNum: number): Array<Array<FamilyGetModel>> {
    let updateModelsList: Array<Array<FamilyGetModel>> = [];

    for (let i: number = 0; i < Math.ceil(updateModels.length / sliceNum); i++) {
      const start: number = i * sliceNum;
      const end: number = start + sliceNum;
      const slicedUpdateModels: Array<FamilyGetModel> = updateModels.slice(start, end);
      updateModelsList.push(slicedUpdateModels);
    }
    return updateModelsList;
  }

  /**
   * ### 家族更新リクエストボディを作成する
   */
  public createFamilyPatchModel(family: FamilyGetModel): FamilyPatchModel {
    return {
      familyName: family.familyName,
      familyTel: family.familyTel,
      familyRelation: family.familyRelation,
      familyGender: family.familyGender,
      familyPosition: family.familyPosition,
      familyBirthDay: family.familyBirthDay,
      familyPermittedFunctions: family.familyPermittedFunctions,
      updated: family.updated
    };
  }

  /**
   * ### 各タブの画面情報再作成
   */
  public async refreshTabs(): Promise<void> {
    const promises: Array<Promise<void>> = [];

    const opinionTab: FamilyPermittedFunctionEditTab | Vue | Element | Vue[] | Element[] = this.$refs.familyOpinionPermittedFunctionEditTab;
    if (opinionTab instanceof FamilyPermittedFunctionEditTab) {
      promises.push(opinionTab.refresh());
    }

    const pictureTab: FamilyPermittedFunctionEditTab | Vue | Element | Vue[] | Element[] = this.$refs.familyPicturePermittedFunctionEditTab;
    if (pictureTab instanceof FamilyPermittedFunctionEditTab) {
      promises.push(pictureTab.refresh());
    }

    await Promise.all(promises);

    return;
  }
}
</script>

<style lang="scss" scoped>
#fmaily-permitted-function-list {
  max-width: 1300px;
  margin-left: auto;
  margin-right: auto;

  .box-header {
    .title {
      font-size: 22px;
      font-weight: bold;
    }

    .setting-btn {
      min-width: 150px;
    }
  }
}
</style>
